import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation, useQueryClient } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { useCreatePsychologist } from "api/usePsychologistQueries";
import { Languages } from "components/psychologist/languages";
import CONSTANTS from "core/constants";
import {
  Psychologist,
  PsychologistProfile,
  User,
  UserWithNullableNames,
} from "core/types";
import { validateNameUpdate, validatePsychologist } from "core/validator";
import { ChangeEvent, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CountryData } from "react-phone-input-2";
import { MarketStrategy } from "strategies/market";
import useMarketStrategy from "strategies/market/useMarketStrategy";
import { styled } from "styled-components";
import { Button, useSnackbar } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { type PhoneFieldChangeValue } from "components/phone-field";
import { useEnabledMarkets } from "../../../core/selectors/marketSelectors";
import { getCountryCode } from "../../../helpers/getCountryCode";
import { useSelectedPsychologistIdParam } from "../../../hooks/useSelectedPsychologistParam";
import PageHeader from "../../lib/pageHeader";
import Comment from "../comment/comment";
import { CreatePsychologistComments } from "../comment/type";
import { ConfirmPhoneNumberDialog } from "../confirmPhoneModal";
import Descriptions from "../description/description";
import { CreateDescriptionMessages } from "../description/type";
import { GenderSection } from "../genderSection";
import HeadlineDropdown from "../headline/headlineDropdown";
import {
  PsychologistValidationKeys,
  UserValidationKeys,
  validationSectionsValidation,
} from "../index.utils";
import { useUpload } from "../profilephoto/useUpload";
import { ProfilePicture } from "../profilePicture";
import ScrollAnchor, { refScroll } from "../scrollAnchor";
import Specialities from "../specialities";
import { LiveArea, Row } from "../styled";
import UserData from "../userData";
import { AbsenceStatusSection } from "../absenceStatusSection";
import { EmploymentTypeSection } from "../employmentTypeSection";

type ChangedValue =
  | PhoneFieldChangeValue
  | ChangeEvent<HTMLInputElement>
  | CreateDescriptionMessages
  | CreatePsychologistComments
  | number[]
  | string;

const isHTMLInputValue = (
  e: ChangedValue,
): e is ChangeEvent<HTMLInputElement> =>
  typeof e === "object" && e !== null && "target" in e;

const isPhoneInputValue = (
  e: ChangedValue,
): e is {
  value: string;
  countryData: CountryData;
  event: React.ChangeEvent<HTMLInputElement>;
} => typeof e === "object" && e !== null && "value" in e;

const getChangedValue = (field: string, e: ChangedValue) => {
  if (isHTMLInputValue(e)) return e.target.value;

  if (field === "phone" && isPhoneInputValue(e)) return e.value;

  return e;
};

type NameValidationError = ReturnType<
  typeof validateNameUpdate
>["nameValidation"][number];

const profileInitialState = {
  psychologistValidation: [] as PsychologistValidationKeys[],
  nameValidation: [] as NameValidationError[],
  profileImageChanged: false,
  updatedPhoneNumber: false,
  openPhoneModal: false,
  isSaving: false,
};

type ProfileState = typeof profileInitialState;

const getEmptyPsychologistProfile = (
  marketStrategy: MarketStrategy,
  defaultLanguageId: number,
): PsychologistProfile => ({
  bigImage: null,
  descriptions: [],
  gender: "M",
  hsa: "",
  languages: [defaultLanguageId],
  phone: "",
  psychologistId: null,
  specialities: [],
  thumbnail: null,
  headlineEnum: marketStrategy.psychologist.headlineKeys[0],
  firstName: null,
  lastName: null,
  absenceStatus: CONSTANTS.PSYCHOLOGIST.ABSENCE_STATUS.ACTIVE,
  employmentType: null,
});

export function Profile({
  user,
  psychologist,
}: {
  user: UserWithNullableNames;
  psychologist: PsychologistProfile | null;
}) {
  const { t } = useTranslation();
  const marketStrategy = useMarketStrategy();
  const enabledMarkets = useEnabledMarkets();
  const activeCountry = enabledMarkets?.find(
    // TODO: We should be able to get this information some other way...
    (market) => market.codeISO2 === getCountryCode(),
  );
  // Ensure psychologist has all required fields from the empty profile
  // This will populate new mandatory fields for existing psychologists
  const psychologistWithDefaults = {
    ...getEmptyPsychologistProfile(
      marketStrategy,
      activeCountry?.defaultLanguageId as number,
    ),
    ...(psychologist || {}),
  };

  const { enqueueSnackbar } = useSnackbar();
  const api = useMindlerApi();
  const queryClient = useQueryClient();
  const { mutateAsync: updateUserName } = useMutation(
    api.users.updateCurrentUserUsername.mutationOptions(),
  );
  const { mutateAsync: createPsychologist } = useCreatePsychologist();
  const [, setSelectedPsychologistId] = useSelectedPsychologistIdParam();
  const [changedUser, changeUser] = useReducer(
    (_: UserWithNullableNames, payload: UserWithNullableNames) => payload,
    user,
  );
  const [changedPsychologist, changePsychologist] = useReducer(
    (_: PsychologistProfile, payload: PsychologistProfile) => payload,
    psychologistWithDefaults,
  );

  const [state, setState] = useReducer(
    (prev: ProfileState, payload: Partial<ProfileState>) => ({
      ...prev,
      ...payload,
    }),
    profileInitialState,
  );
  const { uploadFiles, setFile, state: uploadState } = useUpload();

  const userDataRef = useRef(null);
  const descriptionsRef = useRef(null);
  const commentRef = useRef(null);
  const specialitiesRef = useRef(null);
  const languagesRef = useRef(null);

  const updatePsychologistPhoneConfirm = async () => {
    setState({
      openPhoneModal: false,
      updatedPhoneNumber: false,
    });

    await updatePsychologist();
  };

  const onChange = (field: string) => (e: ChangedValue) => {
    const value = getChangedValue(field, e);
    const newData = {
      ...changedPsychologist,
      [field]: value,
    };

    if (field === "phone") {
      const currentPhoneNumber = psychologistWithDefaults?.phone ?? "";

      setState({
        updatedPhoneNumber:
          currentPhoneNumber !== newData.phone &&
          !!changedPsychologist.psychologistId,
      });
    }

    changePsychologist(newData);
  };

  const invalidateGetPsychologist = () =>
    queryClient.invalidateQueries(api.psychologists.findByUserId.queryFilter());

  const updatePsychologist = async () => {
    setState({ isSaving: true });

    // First check if we need to upload image
    if (state.profileImageChanged) {
      const images = await uploadFiles();

      if (images) {
        // eslint-disable-next-line react-hooks/immutability
        changedPsychologist.thumbnail = images.thumbnailUrl;

        changedPsychologist.bigImage = images.url;
        setState({ profileImageChanged: false });
      }
    }

    const countryCode = getCountryCode();
    const { psychologistValidation } = validatePsychologist(
      changedPsychologist,
      countryCode,
      activeCountry?.defaultLanguageId,
    ) as {
      psychologistValidation: PsychologistValidationKeys[];
    };

    const userPayload = {
      firstName: changedUser.firstName as string,
      lastName: changedUser.lastName as string,
    };
    const { nameValidation } = validateNameUpdate(userPayload, t);

    if (psychologistValidation.length === 0 && nameValidation.length === 0) {
      if (
        state.updatedPhoneNumber &&
        countryCode !== CONSTANTS.SE &&
        !state.openPhoneModal
      ) {
        return setState({
          openPhoneModal: true,
          isSaving: false,
        });
      }

      await createPsychologist(changedPsychologist)
        .then((res: { data: Psychologist }) => {
          setSelectedPsychologistId(res.data.psychologistId);
          return res;
        })
        .then(() => updateUserNames(userPayload))
        .then(() => enqueueSnackbar({ text: t("PROFILE_SAVED") }))
        .then(() => invalidateGetPsychologist())
        .then(() => refScroll(userDataRef))
        .catch((err) => {
          enqueueSnackbar({
            text: t(err.errorLangKey),
            isError: true,
          });
        });
    } else {
      enqueueSnackbar({
        text: t("GENERIC_FORM_ERROR"),
        isError: true,
      });
    }

    setState({
      psychologistValidation,
      nameValidation,
      isSaving: false,
    });

    scrollToFirstError(psychologistValidation, nameValidation);
  };

  const onFileAdded = (file: File) => {
    setFile(file);
    setState({ profileImageChanged: true });
  };

  const updateUserNames = async (
    userPayload: Pick<User, "firstName" | "lastName">,
  ) => {
    await updateUserName(
      {
        firstName: userPayload.firstName ?? "",
        lastName: userPayload.lastName ?? "",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(api.users.getCurrentUser.queryFilter());
        },
        onError: () => {
          enqueueSnackbar({
            text: t("API_ERROR_GENERIC"),
            isError: true,
          });
        },
      },
    );
  };

  const scrollToFirstError = (
    psychologistValidation: PsychologistValidationKeys[],
    nameValidation: NameValidationError[],
  ) => {
    if (userDataRef?.current && nameValidation?.length > 0) {
      return refScroll(userDataRef);
    }
    if (psychologistValidation?.length > 0 && languagesRef?.current) {
      if (
        psychologistValidation?.some((item) =>
          validationSectionsValidation.user.includes(
            item as UserValidationKeys,
          ),
        )
      ) {
        return refScroll(userDataRef);
      }
      if (psychologistValidation?.includes(validationSectionsValidation.gender))
        return refScroll(userDataRef);

      if (
        psychologistValidation?.includes(validationSectionsValidation.comments)
      ) {
        return refScroll(commentRef);
      }

      if (
        psychologistValidation?.includes(
          validationSectionsValidation.descriptions,
        )
      ) {
        return refScroll(descriptionsRef);
      }

      if (
        psychologistValidation?.includes(
          validationSectionsValidation?.specialities,
        )
      ) {
        return refScroll(specialitiesRef);
      }

      if (
        psychologistValidation?.includes(
          validationSectionsValidation?.languages,
        )
      ) {
        return refScroll(languagesRef);
      }
    }
  };
  const userDataUser = {
    ...changedUser,
    firstName: changedUser.firstName ?? user.firstName,
    lastName: changedUser.lastName ?? user.lastName,
  };
  return (
    <div className="{styles.profile}">
      <ConfirmPhoneNumberDialog
        onClose={() => setState({ openPhoneModal: false })}
        isOpen={state.openPhoneModal}
        onConfirm={() => updatePsychologistPhoneConfirm()}
        phoneNumber={changedPsychologist.phone}
      />

      <LiveArea>
        <PageHeader mb={8} hasSensitive={false} translation={t("MY_PROFILE")} />
        <ScrollAnchor ref={userDataRef} />
        <UserData
          psychologist={changedPsychologist}
          user={userDataUser}
          onChange={onChange}
          psychologistValidation={state.psychologistValidation}
          nameValidation={state.nameValidation}
          onChangeUser={changeUser}
        />
        <Row>
          <GenderSection
            value={changedPsychologist.gender}
            setGender={onChange("gender")}
            psychologistValidation={state.psychologistValidation}
          />
        </Row>
        <Row>
          <HeadlineDropdown
            selectedKey={changedPsychologist.headlineEnum}
            onKeyChange={onChange("headlineEnum")}
          />
        </Row>
        <Row>
          <Descriptions
            ref={descriptionsRef}
            psychologist={changedPsychologist}
            onChangeDescriptions={onChange("descriptions")}
            psychologistValidation={state.psychologistValidation}
          />
        </Row>
        <Row>
          <Comment
            psychologist={changedPsychologist}
            ref={commentRef}
            onChangeComments={onChange("comments")}
            psychologistValidation={state.psychologistValidation}
          />
        </Row>
        <Row>
          <AbsenceStatusSection
            value={changedPsychologist.absenceStatus}
            setAbsenceStatus={onChange("absenceStatus")}
            psychologistValidation={state.psychologistValidation}
          />
        </Row>
        <Row>
          <EmploymentTypeSection
            value={changedPsychologist.employmentType}
            setEmploymentType={onChange("employmentType")}
            psychologistValidation={state.psychologistValidation}
          />
        </Row>
        <ScrollAnchor ref={specialitiesRef} />
        <Row>
          <Specialities
            error={state.psychologistValidation.indexOf("specialities") !== -1}
            onChange={onChange("specialities")}
            psychologist={changedPsychologist}
          />
        </Row>
        <ScrollAnchor ref={languagesRef} />
        <Row>
          <Languages
            hasError={state.psychologistValidation.indexOf("languages") !== -1}
            selectedLanguages={changedPsychologist.languages}
            onChange={onChange("languages")}
          />
        </Row>
        <Row>
          <ProfilePicture
            psychologistValidation={state.psychologistValidation}
            psychologist={changedPsychologist}
            onFileAdded={onFileAdded}
            uploadStatus={uploadState}
          />
        </Row>
        <SpaceBetweenContainer>
          <Typography insensitive variant="body">
            {t("REQUIRED_FIELDS")}
          </Typography>

          <Button
            variant="primary"
            onClick={updatePsychologist}
            disabled={state.isSaving}
            id={"cy-save-profile-button"}
          >
            {t("SAVE")}
          </Button>
        </SpaceBetweenContainer>
      </LiveArea>
    </div>
  );
}

const SpaceBetweenContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
`;
